import React from "react";
import { cn } from "@/lib/utils";

/**
 * PageHeader — UI-2A shared foundation component.
 *
 * A single, on-brand (light/warm) page header to standardise the currently
 * divergent header patterns (inline title vs violet hero vs dark navy hero).
 *
 * NOT yet wired into any page — provided as a reusable foundation only.
 * Page migrations are deferred to UI-2B.
 *
 * Props:
 * - title      (string | node)  main H1
 * - subtitle   (string | node)  optional supporting line
 * - eyebrow    (string | node)  optional small label above the title
 * - actions    (node)           optional right-aligned actions (buttons, search)
 * - align      "left" | "center" (default "left" per design guidelines)
 * - className  (string)         extra classes for the wrapper
 */
export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  align = "left",
  className,
  ...props
}) {
  const centered = align === "center";
  return (
    <header
      data-testid="page-header"
      className={cn(
        "w-full border-b border-stone-200 bg-[var(--bg-surface)]",
        className
      )}
      {...props}
    >
      <div
        className={cn(
          "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10",
          "flex flex-col gap-4",
          actions && !centered
            ? "md:flex-row md:items-end md:justify-between"
            : "",
          centered ? "items-center text-center" : "items-start text-left"
        )}
      >
        <div className={cn("flex flex-col gap-2", centered && "items-center")}>
          {eyebrow && (
            <span
              data-testid="page-header-eyebrow"
              className="inline-flex w-fit items-center rounded-full bg-[var(--primary-light)] px-3 py-1 text-xs font-medium text-[var(--primary-text)]"
            >
              {eyebrow}
            </span>
          )}
          <h1
            data-testid="page-header-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--text-main)]"
          >
            {title}
          </h1>
          {subtitle && (
            <p
              data-testid="page-header-subtitle"
              className="max-w-2xl text-base text-[var(--text-muted)]"
            >
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div
            data-testid="page-header-actions"
            className={cn("flex flex-wrap items-center gap-3", centered && "justify-center")}
          >
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

export default PageHeader;
